<script setup lang="ts">
/**
 * Two-state toggle. Built on Reka so the keyboard and focus semantics
 * match a real switch (space/enter to toggle, aria-checked).
 *
 * Visible track is 24px tall, but the tappable surface is 44px — the
 * outer `<button>` is sized for touch (via vertical padding), and a
 * pseudo track inside renders the actual switch graphic. Without that
 * the switch was an exact 24px pixel-hunt on mobile.
 */
import { computed, useAttrs } from 'vue'
import { SwitchRoot, SwitchThumb } from 'reka-ui'

import { cn } from '@/shared/lib/cn'

interface Props {
  modelValue: boolean
  disabled?: boolean
  /** ARIA label when there's no visible <label>. */
  ariaLabel?: string
}
withDefaults(defineProps<Props>(), {
  disabled: false,
  ariaLabel: undefined,
})

const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>()

const attrs = useAttrs()
const classes = computed(() =>
  cn(
    // 44px hit area; the visible track is the centred inner block.
    'group relative inline-flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-md',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:cursor-not-allowed disabled:opacity-50',
    (attrs.class as string) ?? '',
  ),
)
</script>

<template>
  <SwitchRoot
    :model-value="modelValue"
    :disabled="disabled"
    :aria-label="ariaLabel"
    :class="classes"
    data-focus-visible-managed
    @update:model-value="(v) => emit('update:modelValue', v)"
  >
    <!-- Visible 24×44 track inside the hit area. -->
    <span
      class="pointer-events-none relative inline-flex h-6 w-11 items-center rounded-full border-2 border-transparent transition-colors group-data-[state=checked]:bg-primary group-data-[state=unchecked]:bg-muted"
    >
      <SwitchThumb
        class="pointer-events-none block size-5 rounded-full bg-background shadow ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0"
      />
    </span>
  </SwitchRoot>
</template>
